import React, { Component } from "react";
/* import PropTypes from 'prop-types';*/
import { getEmptyImage } from "react-dnd-html5-backend";
import { DragSource } from "react-dnd";
import GridCellCropResizeHandlerContainer from "./gridCellCropResizeHandler/GridCellCropResizeHandlerContainer";
import { Image, Video } from "views/components";
import ResizeBox from "views/components/Editor/ResizeBox";
import { getPlaceholderSize } from "views/components/Editor/elements/grid/imageInstructionUtils";
import InnerImageRotateHandle from "views/components/Editor/rotateHandler/InnerImageRotateHandle";
import { isVideo } from "lib/isVideo";

const GridMaskingDraggableImageSource = {
  beginDrag(props, monitor, component) {
    props.startGridCellCropMoving();
    return {};
  },
  endDrag(props, monitor) {
    const differenceFromInitialOffset =
      monitor.getDifferenceFromInitialOffset();

    props.finishGridCellCropMoving({ differenceFromInitialOffset });
  }
};

function collect(connect, monitor) {
  return {
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    isDragging: monitor.isDragging()
  };
}

export class GridMaskingDraggableImage extends Component {
  constructor(props) {
    super(props);
    // Zero-size marker positioned at the cell-image centre inside the
    // rotating wrap. The wrap's transform-origin is the same point, so
    // this marker's screen position is invariant under the wrap's
    // rotation — exactly what InnerImageRotateHandle needs as a pivot.
    this.rotatePivotRef = React.createRef();
  }

  componentDidMount() {
    const { connectDragPreview } = this.props;
    if (connectDragPreview) {
      connectDragPreview(getEmptyImage(), {
        captureDraggingState: true
      });
    }
  }

  cancelClick(event) {
    event.stopPropagation();
    event.preventDefault();
  }

  render() {
    const {
      connectDragSource,
      pageOffset,
      gridElement,
      gridCell,
      isCropping,
      zoom,
      offsetLeft,
      offsetTop,
      onInnerImageRotationPreview,
      onInnerImageRotationCommit
    } = this.props;

    const placeholderSize = getPlaceholderSize(gridCell, gridElement);

    const transformedLeft =
      gridCell.scaleX === 1
        ? gridCell.left
        : placeholderSize.width - (gridCell.width + gridCell.left);
    const transformedTop =
      gridCell.scaleY === 1
        ? gridCell.top
        : placeholderSize.height - (gridCell.height + gridCell.top);

    const rotationDeg = gridCell.rotation || 0;

    // Mirror the action-bar "angle" gating. Two failure modes:
    //   1. Element has the "angle" restriction — rotation is locked at the
    //      template level, so the handle is *removed entirely* below to
    //      avoid a phantom disabled affordance hovering over the crop UI.
    //   2. Cell is empty / still in preview state — rotation makes no
    //      sense yet but will once the cell is populated. Render the
    //      handle disabled in that case.
    const restrictions = gridElement?.restrictions ?? [];
    const isInnerImageRotateRestricted = restrictions.includes("angle");
    const innerImageRotateDisabled =
      isInnerImageRotateRestricted ||
      !(gridCell && gridCell.id && !gridCell.isPreview);

    // The image sits inside the cell-local clip box at
    // (transformedLeft * zoom, transformedTop * zoom) with size
    // (gridCell.width * zoom, gridCell.height * zoom). Its centre — the
    // inner rotation pivot — is therefore at centre-X, centre-Y in
    // cell-local coords.
    const cellWidthZoomed = gridCell.width * zoom;
    const cellHeightZoomed = gridCell.height * zoom;
    const cellTransformedLeftZoomed = transformedLeft * zoom;
    const cellTransformedTopZoomed = transformedTop * zoom;
    const pivotX = cellTransformedLeftZoomed + cellWidthZoomed / 2;
    const pivotY = cellTransformedTopZoomed + cellHeightZoomed / 2;

    const Handler = props => {
      return (
        <GridCellCropResizeHandlerContainer
          gridCell={gridCell}
          {...props}
          gridElement={gridElement}
        />
      );
    };

    const buildCellTransform = () => {
      const parts = [
        `translate(${cellTransformedLeftZoomed}px, ${cellTransformedTopZoomed}px)`
      ];
      if (rotationDeg) parts.push(`rotate(${rotationDeg}deg)`);
      parts.push(`scaleX(${gridCell.scaleX})`, `scaleY(${gridCell.scaleY})`);
      return parts.join(" ");
    };

    const showRotateHandle =
      typeof onInnerImageRotationPreview === "function" &&
      typeof onInnerImageRotationCommit === "function" &&
      !isInnerImageRotateRestricted;

    // The handle wrapper sits inside two stacked transforms:
    //   outer wrap: R(gridElement.angle) · S_x(gridEl.scaleX) · S_y(gridEl.scaleY)
    //   inner wrap: R(rotationDeg)       · S_x(gridCell.scaleX) · S_y(gridCell.scaleY)
    // To keep the icon glyph upright on screen, InnerImageRotateHandle's
    // counter-transform has to invert the *combined* matrix — countering
    // only the cell-level transforms (the previous behaviour) leaves the
    // glyph tilted/mirrored relative to the screen whenever the outer grid
    // element is itself rotated or flipped.
    //
    // For uniform-sign flips (both axes 1, both axes -1, or no flip on
    // outer) the inner rotation passes through the outer flip unchanged.
    // For odd-parity flips (exactly one axis flipped on the outer) the
    // outer flip conjugates the inner rotation: S · R(θ) · S = R(-θ),
    // so the *effective* inner rotation as observed after the outer
    // transform reverses sign. That collapse lets the combined matrix
    // simplify to a single rotation followed by a single composed scale,
    // which is exactly the form InnerImageRotateHandle's counter accepts.
    const gridElementScaleX = gridElement.scaleX ?? 1;
    const gridElementScaleY = gridElement.scaleY ?? 1;
    const outerAngle = gridElement.angle || 0;
    const outerFlipParity = gridElementScaleX * gridElementScaleY;
    const effectiveCellRotation =
      outerFlipParity === -1 ? -rotationDeg : rotationDeg;
    const composedCounterRotateDeg = outerAngle + effectiveCellRotation;
    const composedCounterScaleX = gridElementScaleX * gridCell.scaleX;
    const composedCounterScaleY = gridElementScaleY * gridCell.scaleY;

    return connectDragSource(
      <div
        style={{
          position: "absolute",
          top: pageOffset.top + gridElement.top * zoom,
          left: pageOffset.left + gridElement.left * zoom,
          height: gridElement.height * zoom,
          width: gridElement.width * zoom,
          zIndex: 4,
          opacity: isCropping ? 0 : 1,
          transform: [
            `rotate(${gridElement.angle}deg)`,
            `scaleX(${gridElement.scaleX})`,
            `scaleY(${gridElement.scaleY})`
          ].join(" ")
        }}
      >
        <div
          onClick={this.cancelClick}
          style={{
            position: "absolute",
            top: offsetTop * zoom,
            left: offsetLeft * zoom,
            zIndex: 4,
            opacity: isCropping ? 0 : 1,
            transform: buildCellTransform(),
            ...(rotationDeg ? { transformOrigin: "center" } : {})
          }}
        >
          {isVideo(gridCell.previewSrc) ? (
            <Video
              src={gridCell.previewSrc}
              height={gridCell.height * zoom}
              width={gridCell.width * zoom}
              filter="brightness(0.5)"
              isLandscape={true}
              autoPlay
            />
          ) : (
            <Image
              src={gridCell.previewSrc}
              height={gridCell.height * zoom}
              width={gridCell.width * zoom}
              filter="brightness(0.5)"
              isLandscape={true}
            />
          )}
          <ResizeBox isResizing={false} Handler={Handler} isCropping={true} />
        </div>
        {/* Rotate handle — sibling to the image so it's outside the image's
            stacking context. Rotates with the image (so it orbits the
            cell's bounds); counter-rotates its icon to stay upright. The
            zero-size pivot marker sits at the cell-image centre in
            cell-local coords; the wrap rotates around the same point, so
            the marker's screen position is invariant under rotation. */}
        {showRotateHandle && (
          <div
            style={{
              position: "absolute",
              top: offsetTop * zoom,
              left: offsetLeft * zoom,
              zIndex: 20,
              pointerEvents: "none",
              transform: rotationDeg
                ? `rotate(${rotationDeg}deg) scaleX(${gridCell.scaleX}) scaleY(${gridCell.scaleY})`
                : `scaleX(${gridCell.scaleX}) scaleY(${gridCell.scaleY})`,
              transformOrigin: `${pivotX}px ${pivotY}px`
            }}
          >
            <div
              ref={this.rotatePivotRef}
              style={{
                position: "absolute",
                left: `${pivotX}px`,
                top: `${pivotY}px`,
                width: 0,
                height: 0
              }}
            />
            <InnerImageRotateHandle
              anchorLeft={pivotX}
              anchorTop={cellTransformedTopZoomed + cellHeightZoomed}
              counterRotateDeg={composedCounterRotateDeg}
              counterScaleX={composedCounterScaleX}
              counterScaleY={composedCounterScaleY}
              pivotRef={this.rotatePivotRef}
              rotation={rotationDeg}
              disabled={innerImageRotateDisabled}
              onRotationPreview={onInnerImageRotationPreview}
              onRotationCommit={onInnerImageRotationCommit}
            />
          </div>
        )}
      </div>
    );
  }
}

/* TextMaskingDraggableImage.propTypes = propTypes;*/

// Export the wrapped component:
export default DragSource(
  "GRID_CELL_IMAGE",
  GridMaskingDraggableImageSource,
  collect
)(GridMaskingDraggableImage);
